<template>
  <div class="flex-1-1 BACKGROUND bg-gray20">
    <div class="MESSAGES">
      <div v-if="!isMobile || !hasSelected" class="SIDEBAR" selenium="consumer-inbox-list">
        <conversation-list
            v-infinite-scroll="{ offset: 200, promise: hasMore && !loading && loadMore }"
            :list="list"
            :selected-key="selectedKey"
            :loading="loading"
            @select="select"/>
      </div>
      <div v-if="!isMobile || hasSelected || !list.length" class="BODY">
        <button
            v-if="isMobile && hasSelected"
            class="btn-ghostfilled bd-b-depot btn-sm w-100 jc-start flex-0-0"
            @click="closeConversation">
          <vk-icon name="chevronleft" size="16" class="m-r-1 c-depot"/>
          Messages
        </button>
        <template v-if="selectedProviderId">
          <conversation-header
              :job="workOrder"
              :skills-profile-id="selectedProviderId"/>
          <messages-view
              v-bind="getInfo(workOrder.order_hash, selectedProviderId)"
              :key="selectedKey"
              :job="workOrder"
              :skills-profile-id="selectedProviderId"
              class="CHAT"/>
        </template>
        <div v-else-if="hasSelected && workOrder" class="INFO" selenium="consumer-pro-not-found">
          <span class="text-bold text-lg">We couldn't open this conversation</span>
          <span class="c-gray60">This Pro is no longer matched to the request.</span>
        </div>
        <div v-else-if="hasSelected" class="INFO">
          <loading-indicator :size="48"/>
        </div>
        <div v-else-if="loaded && !list.length" class="INFO" selenium="consumer-no-messages">
          <span class="text-bold text-lg">You have no messages</span>
          <span class="m-b-3 c-gray60">Message a Pro from My Requests and it will show here.</span>
          <router-link :to="{ name: 'consumer::dashboard' }" class="btn-secondary btn-sm">
            Go to My Requests
          </router-link>
        </div>
        <div v-else-if="loaded" class="INFO">
          <span class="text-bold text-lg">Select a message</span>
          <span class="c-gray60">Choose a conversation to reach out to your Pros.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, isToday, differenceInDays } from 'date-fns';
import { mapActions, mapGetters } from 'vuex';
import { getProUserId } from '@/store/modules/consumer/modules/inbox';
import workOrderMixin from '@/mixins/work-order';
import addUTCTimeZoneDesignator from '@/utils/add-utc-time-zone-designator';
import MessagesView from '@/views/Consumer/views/Messages/components/MessagesView';
import ConversationHeader from './components/ConversationHeader';
import ConversationList from './components/ConversationList';

// The Pro is the other side of the conversation (see consumer/inbox store)
const proUserIdOf = message => getProUserId(message);

export default {
  name: 'consumer-inbox',
  title: 'Messages',
  components: { ConversationHeader, ConversationList, MessagesView },
  mixins: [workOrderMixin],
  props: {
    jobId: {
      type: String,
      default: null,
    },
    proUserId: {
      type: [String, Number],
      default: null,
    },
  },
  data () {
    return {
      loading: false,
      loaded: false,
    };
  },
  computed: {
    ...mapGetters('consumer/inbox', ['getInbox', 'getTotal', 'hasUnreadMessage']),
    ...mapGetters('consumer/messages', ['getInfo']),
    isMobile () {
      return this.$device.isMobile;
    },
    hasSelected () {
      return Boolean(this.jobId && this.proUserId);
    },
    selectedKey () {
      return this.hasSelected ? `${this.jobId}-${this.proUserId}` : '';
    },
    // service_provider_id of the selected Pro, taken from the work order's matches. A Pro who is
    // no longer matched still has messages, so fall back to the id on the Pro's inbox message.
    selectedProviderId () {
      if (!this.hasSelected || !this.workOrder) { return null; }
      const match = this.workOrder.matches.find(
        item => item && String(item.service_provider_user_identifier) === String(this.proUserId)
      );
      if (match) { return match.service_provider_id; }
      const message = this.getInbox.find(item => (
        item.user_friendly_identifier === this.jobId
        && String(proUserIdOf(item)) === String(this.proUserId)
      ));
      return message ? message.service_provider_id : null;
    },
    // As soon as the Pro's service_provider_id is known, the opened conversation's inbox
    // message(s) can be marked as seen on the server (no need to wait for the chat to load)
    seenTarget () {
      if (!this.selectedProviderId) { return null; }
      const messages = this.getInbox.filter(message => (
        message.user_friendly_identifier === this.jobId
        && String(proUserIdOf(message)) === String(this.proUserId)
      ));
      return messages.length
        ? {
          messages,
          jobId: this.jobId,
          proUserId: this.proUserId,
          skillsProfileId: this.selectedProviderId,
        }
        : null;
    },
    hasMore () {
      return this.getInbox.length < this.getTotal;
    },
    list () {
      return this.getInbox.map(message => ({
        key: `${message.user_friendly_identifier}-${proUserIdOf(message)}`,
        jobId: message.user_friendly_identifier,
        proUserId: proUserIdOf(message),
        name: this.proName(message),
        message: message.message,
        date: this.formatDate(message.time_created),
        unread: this.hasUnreadMessage(message.user_friendly_identifier, proUserIdOf(message)),
      }));
    },
  },
  watch: {
    seenTarget: {
      immediate: true,
      handler (target) {
        if (target) {
          this.markConversationRead(target);
        }
      },
    },
    jobId: {
      immediate: true,
      handler (jobId) {
        if (jobId) {
          this.fetchWorkOrder({ jobId, includePublicProfiles: true }).catch((e) => {
            console.error(e); // eslint-disable-line no-console
          });
        }
      },
    },
  },
  async mounted () {
    this.loading = true;
    try {
      await this.refreshInbox();
      if (this.hasSelected) {
        await this.markAsRead({ jobId: this.jobId, proUserId: this.proUserId });
      }
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    } finally {
      this.loading = false;
      this.loaded = true;
    }
  },
  methods: {
    ...mapActions('consumer/inbox', [
      'refreshInbox',
      'fetchInbox',
      'markAsRead',
      'markConversationRead',
    ]),
    select (item) {
      this.markAsRead({ jobId: item.jobId, proUserId: item.proUserId });
      this.$router.push({
        name: 'consumer::inbox',
        params: { jobId: item.jobId, proUserId: String(item.proUserId) },
      });
    },
    closeConversation () {
      this.$router.push({ name: 'consumer::inbox' });
    },
    async loadMore () {
      this.loading = true;
      try {
        await this.fetchInbox({ params: { from: this.getInbox.length }, reason: 'list' });
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      } finally {
        this.loading = false;
      }
    },
    proName (message) {
      // The pro is the other side of the conversation
      return message.sender_type === 'pro'
        ? message.sender_full_name
        : message.recipient_full_name;
    },
    formatDate (date) {
      const day = new Date(addUTCTimeZoneDesignator(date));
      if (differenceInDays(new Date(), day) >= 5) { return format(day, 'MMM dd'); }
      return isToday(day) ? 'Today' : format(day, 'cccc');
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  .MESSAGES {
    width: 100%;
    // Fit under the header (~110px) so the cards are fully visible without scrolling the page
    height: calc(100vh - 170px);
    min-height: 480px;
    margin: 2 * $unit auto;
    padding: 0 2 * $unit;
    max-width: 1200px;
    display: flex;
    @include mobile-only() {
      height: calc(100vh - 130px);
      // dvh follows the visible viewport (browser URL bar showing/hiding)
      height: calc(100dvh - 130px);
      min-height: 420px;
      margin: 0;
      padding: 0;
    }
  }
  .SIDEBAR {
    @include responsive(width, 100%, 320px, 400px);
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    margin-right: 2 * $unit;
    background-color: white;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    min-height: 0;
    @include mobile-only() {
      margin-right: 0;
    }
  }
  .BODY {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    background-color: white;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  }
  .CHAT {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }
  .INFO {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
</style>
